import { useBreakpoint } from '@consigli/hooks';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaCodeCompare,
  FaFileLines,
  FaHouse,
  FaFolderOpen,
  FaPencil,
  FaFileArrowDown,
  FaFileArrowUp,
  FaLocationDot,
  FaClipboardList,
} from 'react-icons/fa6';
import { FiSettings } from 'react-icons/fi';
import { GoHome } from 'react-icons/go';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { IoCalendarNumberOutline } from 'react-icons/io5';
import { IoChatbubbles } from 'react-icons/io5';
import { LuFileSpreadsheet } from 'react-icons/lu';
import { Link, useLocation } from 'react-router';

interface WorkspaceTabsItemProps {
  name: string;
  href: string;
}

function mapTabNameToIcon(tabName: string) {
  const iconMap: Record<string, JSX.Element | null> = {
    'servicetabs.summary': <FaClipboardList />,
    'servicetabs.overview': <FaHouse />,
    'servicetabs.risks-insights-gaps': <FaHouse />,
    'servicetabs.bid-comparison': <FaCodeCompare />,
    'servicetabs.findings': <FaPencil />,
    'servicetabs.documents': <FaFileLines />,
    'servicetabs.propchat': <IoChatbubbles />,
    'servicetabs.folders': <FaFolderOpen />,
    'servicetabs.dashboard': <GoHome />,
    'servicetabs.meetings': <IoCalendarClearOutline />,
    'servicetabs.calendar': <IoCalendarNumberOutline />,
    'servicetabs.templates': <LuFileSpreadsheet />,
    'servicetabs.settings': <FiSettings />,
    'servicetabs.reports': <FaFileArrowDown />,
    'servicetabs.location': <FaLocationDot />,
    'optimization-tabs.input': <FaFileArrowUp />,
    'optimization-tabs.results': <FaFileArrowDown />,
    'structural-tabs.input': <FaFileArrowUp />,
    'structural-tabs.results': <FaFileArrowDown />,
    'schedule-check-tabs.comparison': <FaCodeCompare />,
    'schedule-check-tabs.documents': <FaFileLines />,
  };

  return iconMap[tabName] || null; // Return null for unknown tabName
}

export const WorkspaceTabsItem: FC<WorkspaceTabsItemProps> = ({ name, href }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isActive = location.pathname.startsWith(href);
  const isLargeScreen = useBreakpoint(1024); // true when >= 1024px
  const icon = mapTabNameToIcon(name);

  return (
    <Link
      className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 ease-in-out group ${
        isActive
          ? 'bg-day-light-3 text-gray-900'
          : 'text-gray-600 hover:bg-day-light-3 hover:text-gray-900 cursor-pointer'
      }`}
      to={href}
    >
      {/* Show icon only on small screens (< 1024px) */}
      {!isLargeScreen && icon && <div className="text-gray-600 text-lg">{icon}</div>}
      {/* Show text only on large screens (>= 1024px) */}
      {isLargeScreen && (
        <span className="font-medium whitespace-nowrap text-sm 2xl:text-base">{t(name)}</span>
      )}
    </Link>
  );
};
